import escapeHtml from "escape-html";
import type { EChartsOption } from "echarts";
import {
  BAR_BORDER_RADIUS_VERTICAL,
  buildAxisLabelStyle,
  buildLegendTextStyle,
  buildTooltipStyle,
  buildTooltipValueStyle,
  CHART_FONT_FAMILY,
  chartGridLineColor,
  CONTAIN_AXIS_LABELS,
  donutRadius,
  DONUT_SLICE_GAP_DEGREES,
  formatImpressionAxis,
  formatShare,
  getChartTextColors,
  IMPRESSION_FORMATTER,
  LEGEND_BASE,
  PERCENT_FORMATTER,
} from "@/pages/CampaignOverviewPage/charts/chartHelpers";
import type { PillBarListRow } from "@/components/PillBarList";
import { niceAxisMax } from "@/pages/CampaignOverviewPage/charts/chartOptions";
import { getChartPalette } from "@/pages/CampaignOverviewPage/charts/chartPalette";
import {
  groupByCreative,
  selectTopCreatives,
  groupByCreativeSize,
  weightedCtr,
} from "@/pages/CampaignOverviewPage/utils/creativeGrouping";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";

const OTHER_LABEL = TEXT.campaignOverview.labels.other;

const CREATIVE_TEXT = TEXT.campaignOverview.creativePerformance;

// Line stroke width for series (in ECharts virtual pixels).
const LINE_STROKE_WIDTH = 2.5;

export const CREATIVE_TOP_N = 10;

const MIN_CTR_AXIS_DECIMALS = 2;
const MAX_CTR_AXIS_DECIMALS = 4;
const CTR_AXIS_SPLITS = 5;

// Tick labels need enough decimals to tell neighbouring ticks apart: with a
// peak CTR of 0.03% two decimals would print "0.03%" three times in a row.
const ctrAxisDecimals = (interval: number): number =>
  Math.min(
    MAX_CTR_AXIS_DECIMALS,
    Math.max(MIN_CTR_AXIS_DECIMALS, Math.ceil(-Math.log10(interval)))
  );

const formatCtrAxis =
  (decimals: number) =>
  (value: number | string): string =>
    `${Number(value).toFixed(decimals)}%`;

// Rows for the Impressions by Creative bars, largest first. showAll toggles
// between the top-N cap and every creative.
export const buildCreativeBarRows = (
  rows: PerformanceReportRow[],
  showAll: boolean
): PillBarListRow[] => {
  const all = groupByCreative(rows);
  return (showAll ? all : all.slice(0, CREATIVE_TOP_N)).map(
    ({ id, impressions, name }) => ({
      id,
      label: name,
      tooltip: [
        { label: CREATIVE_TEXT.tooltipName, value: name },
        { label: CREATIVE_TEXT.tooltipId, value: id || "—" },
        {
          isMetric: true,
          label: CREATIVE_TEXT.legendImpressions,
          value: IMPRESSION_FORMATTER.format(impressions),
        },
      ],
      value: impressions,
    })
  );
};

// Chart 2 — Impressions by Creative Size: flat donut in part-to-whole order.
// Top 5 sizes shown individually, the rest collapsed into a gray "Other" so
// the chart never renders more than 6 slices.
// Matches SIZE_DONUT_HEIGHT (13.75rem) in CreativePerformanceWidget.
const SIZE_DONUT_PX = 220;
const SIZE_SLICE_LIMIT = 5;

interface SizeSlice {
  color: string;
  name: string;
  share: number;
  value: number;
  otherItems?: { impressions: number; size: string }[];
}

export const buildSizeSlices = (
  rows: PerformanceReportRow[],
  isDark: boolean
): SizeSlice[] => {
  const palette = getChartPalette(isDark);
  const grouped = groupByCreativeSize(rows);
  const remaining = grouped.slice(SIZE_SLICE_LIMIT);
  const otherImpressions = remaining.reduce(
    (sum, item) => sum + item.impressions,
    0
  );
  const slices = grouped
    .slice(0, SIZE_SLICE_LIMIT)
    .map(({ impressions, size }, index) => ({
      color: palette.partToWhole[index],
      name: size,
      share: 0,
      value: impressions,
    }));
  const withOther =
    otherImpressions > 0
      ? [
          ...slices,
          {
            color: palette.other,
            name: OTHER_LABEL,
            otherItems: remaining,
            share: 0,
            value: otherImpressions,
          },
        ]
      : slices;
  const total = withOther.reduce((sum, { value }) => sum + value, 0);
  return withOther.map((slice) => ({
    ...slice,
    share: total > 0 ? slice.value / total : 0,
  }));
};

// Center label sits on the largest slice (always first — data is sorted desc)
// and is the only slice label drawn: solid charcoal 700 value, muted caption.
// ECharts centers the label block as a whole, which would put the percentage
// above the ring's center; an invisible spacer line as tall as the caption sits
// above it so the percentage itself lands on the center and the caption below.
const CENTER_CAPTION_LINE_HEIGHT = 16;

// ECharts rich text isn't HTML, so escaping would print "&amp;" literally; the
// only characters that can break its `{style|text}` syntax are the braces and
// the bar.
const richTextSafe = (text: string): string => text.replace(/[{}|]/g, "");

const buildCenterLabel = (slice: SizeSlice | undefined, isDark: boolean) => {
  const { primary, secondary } = getChartTextColors(isDark);
  return {
    color: primary,
    fontFamily: CHART_FONT_FAMILY,
    formatter: slice
      ? `{spacer|\u200b}\n{value|${formatShare(slice.share)}}\n{caption|${richTextSafe(slice.name)}}`
      : "",
    position: "center" as const,
    rich: {
      caption: {
        color: secondary,
        fontSize: 11,
        lineHeight: CENTER_CAPTION_LINE_HEIGHT,
      },
      spacer: { fontSize: 11, lineHeight: CENTER_CAPTION_LINE_HEIGHT },
      value: {
        color: primary,
        fontFamily: CHART_FONT_FAMILY,
        fontSize: 28,
        fontWeight: 700,
        lineHeight: 36,
      },
    },
    show: true,
  };
};

export const buildImpressionsBySizeOption = (
  rows: PerformanceReportRow[],
  isDark: boolean
): EChartsOption => {
  const slices = buildSizeSlices(rows, isDark);
  const valueStyle = buildTooltipValueStyle(isDark);

  return {
    series: [
      {
        data: slices.map((slice, index) => ({
          itemStyle: { color: slice.color },
          label: index === 0 ? buildCenterLabel(slice, isDark) : undefined,
          name: slice.name,
          otherItems: slice.otherItems,
          value: slice.value,
        })),
        label: { show: false },
        labelLine: { show: false },
        padAngle: DONUT_SLICE_GAP_DEGREES,
        radius: donutRadius(SIZE_DONUT_PX),
        startAngle: 90,
        type: "pie",
      },
    ],
    tooltip: {
      ...buildTooltipStyle(isDark),
      formatter: (params: unknown) => {
        const item = params as {
          data?: { otherItems?: SizeSlice["otherItems"] };
          marker: string;
          name: string;
          percent: number;
          value: number;
        };

        if (item.name === OTHER_LABEL && item.data?.otherItems) {
          const breakdown = item.data.otherItems
            .map(
              ({ size, impressions }) =>
                `${escapeHtml(size)}: ${IMPRESSION_FORMATTER.format(impressions)}`
            )
            .join("<br/>");
          return `${item.marker}<b style="${valueStyle}">Other</b> (${item.percent}%)<br/>${breakdown}`;
        }

        return `${item.marker}${escapeHtml(item.name)}: <b style="${valueStyle}">${IMPRESSION_FORMATTER.format(item.value)}</b> (${item.percent}%)`;
      },
      trigger: "item",
    },
  };
};

const formatComboValue = (seriesName: string, value: number | null): string => {
  if (value === null) return "—";
  return seriesName === CREATIVE_TEXT.legendCtr
    ? `${PERCENT_FORMATTER.format(value)}%`
    : IMPRESSION_FORMATTER.format(value);
};

// CTR by creative: impressions as vertical bars on the left axis, CTR as a
// solid orange line on the right axis. Defaults to the top-N creatives by
// impressions so x-axis labels stay readable; showAll lifts the cap.
export const buildCreativeComboOption = (
  rows: PerformanceReportRow[],
  showAll: boolean,
  isDark: boolean,
  leaderKey: string | null = null
): EChartsOption => {
  const palette = getChartPalette(isDark);
  const axisLabelStyle = buildAxisLabelStyle(isDark);
  const valueStyle = buildTooltipValueStyle(isDark);
  const all = groupByCreative(rows);
  const visible = showAll
    ? all
    : selectTopCreatives(all, CREATIVE_TOP_N, leaderKey).creatives;
  const names = visible.map((entry) => entry.name);
  // Fixed maxes with the same split count keep both axes' gridlines aligned.
  const impressionsMax = niceAxisMax(
    Math.max(0, ...visible.map(({ impressions }) => impressions)),
    CTR_AXIS_SPLITS
  );
  const ctrMax = niceAxisMax(
    Math.max(
      0,
      ...visible.map(({ clicks, impressions }) =>
        weightedCtr(clicks, impressions)
      )
    ),
    CTR_AXIS_SPLITS
  );

  return {
    grid: {
      bottom: 56,
      ...CONTAIN_AXIS_LABELS,
      left: 56,
      right: 56,
      top: 16,
    },
    // Swatches are flat even though the bars draw a gradient.
    legend: {
      ...LEGEND_BASE,
      data: [
        {
          icon: "roundRect",
          itemStyle: { color: palette.primary },
          name: CREATIVE_TEXT.legendImpressions,
        },
        {
          icon: "circle",
          itemStyle: { color: palette.orange },
          name: CREATIVE_TEXT.legendCtr,
        },
      ],
      textStyle: buildLegendTextStyle(isDark),
    },
    series: [
      {
        data: visible.map(({ id, impressions }) => ({
          id,
          value: impressions,
        })),
        itemStyle: {
          borderRadius: BAR_BORDER_RADIUS_VERTICAL,
          color: palette.barGradientV,
        },
        name: CREATIVE_TEXT.legendImpressions,
        type: "bar",
        yAxisIndex: 0,
      },
      {
        // null (not 0) where a creative has no impressions, so the line
        // breaks instead of drawing through a missing value.
        connectNulls: false,
        data: visible.map(({ clicks, id, impressions }) => ({
          id,
          value: impressions > 0 ? weightedCtr(clicks, impressions) : null,
        })),
        itemStyle: { color: palette.orange },
        lineStyle: { color: palette.orange, width: LINE_STROKE_WIDTH },
        name: CREATIVE_TEXT.legendCtr,
        symbol: "circle",
        symbolSize: 8,
        type: "line",
        yAxisIndex: 1,
      },
    ],
    tooltip: {
      ...buildTooltipStyle(isDark),
      confine: true,
      formatter: (params: unknown) => {
        const items = params as {
          data: { id?: string; value: number | null };
          marker: string;
          name: string;
          seriesName: string;
          value: number | null;
        }[];
        const first = items[0];
        if (!first) return "";
        const name = escapeHtml(first.name);
        const id = first.data?.id ? escapeHtml(first.data.id) : "—";
        const body = items
          .map((item) => {
            const val = formatComboValue(item.seriesName, item.value);
            return `${item.marker}${item.seriesName}: <b style="${valueStyle}">${val}</b>`;
          })
          .join("<br/>");
        return [`<b>Name:</b> ${name}`, `<b>ID:</b> ${id}`, body].join("<br/>");
      },
      trigger: "axis",
    },
    xAxis: {
      axisLabel: {
        ...axisLabelStyle,
        // With every creative shown, forcing all labels would overprint them.
        interval: showAll ? "auto" : 0,
        overflow: "truncate",
        rotate: -20,
        width: 80,
      },
      axisLine: { lineStyle: { color: chartGridLineColor(isDark) } },
      axisTick: { show: false },
      data: names,
      type: "category",
    },
    yAxis: [
      {
        axisLabel: { ...axisLabelStyle, formatter: formatImpressionAxis },
        max: impressionsMax,
        min: 0,
        splitLine: { lineStyle: { color: chartGridLineColor(isDark) } },
        splitNumber: CTR_AXIS_SPLITS,
        type: "value",
      },
      {
        axisLabel: {
          ...axisLabelStyle,
          formatter: formatCtrAxis(ctrAxisDecimals(ctrMax / CTR_AXIS_SPLITS)),
        },
        max: ctrMax,
        min: 0,
        position: "right",
        splitLine: { show: false },
        splitNumber: CTR_AXIS_SPLITS,
        type: "value",
      },
    ],
  };
};
